/**
 * Licensed Materials - Property of IBM
 * IBM Watson (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
	SVGIcon,
	Label,
	Link,
	Multilinetruncatedtext,
	Tooltip
} from '@ba/ba-ui-carbon-toolkit';
import StringResources from './../nls/StringResources';
import VisualizationContainer from './VisualizationContainer';
import _ from 'underscore';
import classnames from 'classnames';
import addNew16 from '@ba/ba-graphics/dist/icons-js/add_32';
import confirm16 from '@ba/ba-graphics/dist/icons-js/confirm_16';

import CSSTransition from 'react-transition-group/CSSTransition';

import { observable } from 'mobx';

import './visPreview.scss';

const CLASS_NAME = 'vis-recommender__preview';

const cloneItem = (item) => {
	return JSON.parse(JSON.stringify(item));
};

@observer
class VisualizationPreview extends Component {
	@observable name = '';
	constructor(props) {
		super(props);

		this.name = this.props.item.name;

		this._pendingAddTimeoutId = undefined;

		this.state = {
			adding: this.props.adding
		};
	}

	render() {

		const isHorizontal = this.props.horizontal;

		const top = this.renderTop();
		const bottom = this.renderBottom();

		if (this.props.isNewLayoutEnabled) {
			const wrapperClassNames = classnames({
				[`${CLASS_NAME}--wrapper__horizontal`]: isHorizontal,
				[`${CLASS_NAME}--wrapper`]: !isHorizontal
			});

			const classNames = classnames(
				CLASS_NAME,
				`${CLASS_NAME}__size-${this.props.size}`,
				{
					[`${CLASS_NAME}--horizontal`]: isHorizontal,
					[`${CLASS_NAME}--vertical`]: !isHorizontal
				}
			);
			let tooltipText = this.props.add ? StringResources.get('viewAdd') : StringResources.get('change_vis');
			return (
				<div className={wrapperClassNames}>
					{this.renderStaticTitle()}
					<Tooltip
						title={tooltipText}
						orient='top'
						display='block'
					>
						<div className={classNames}
							onClick={this._onSelect.bind(this)}
							onKeyDown={this._onKeyDown.bind(this)}
							tabIndex={0}
							walkme-data-id={`vis-recommender-preview-${this.props.index}`}
						>
							{this.name && <Multilinetruncatedtext className='vis-recommender__preview__visTitle'  numLines={2} value={this.name} />}
							{top}
							{this._renderCompoundPreviewItem()}
							{bottom}
						</div>
					</Tooltip>
				</div>
			);
		} else {
			const classNames = classnames(
				CLASS_NAME,
				`${CLASS_NAME}__size-${this.props.size}`,
				{
					[`${CLASS_NAME}--horizontal`]: isHorizontal,
					[`${CLASS_NAME}--old_vertical`]: !isHorizontal
				}
			);

			return (
				<div className={classNames}
					onClick={this._onSelect.bind(this)}
					onKeyDown={this._onKeyDown.bind(this)}
					tabIndex={0}
				>
					{top}
					{this._renderCompoundPreviewItem()}
					{bottom}
				</div>
			);
		}
	}

	renderStaticTitle() {
		const {
			item: {
				staticTitle
			}
		} = this.props;

		if (!staticTitle) {
			return;
		}

		return (
			<div className={`${CLASS_NAME}__static-title`}>
				{staticTitle}
			</div>
		);
	}

	_renderCompoundPreviewItem() {

		const {
			item: {
				widgets = []
			}
		} = this.props;

		const classNames = classnames(
			`${CLASS_NAME}__preview-box`
		);

		return (
			<div className={classNames}>
				{widgets.map((widget, index) => {
					if (widget.spec) {
						return this._renderSinglePreviewItem(widget, index);
					} else {
						return this._renderSingleEmptyItem();
					}
				})}
			</div>
		);
	}

	_renderSingleEmptyItem() {
		return (
			<div className="vis-recommender__container">
				<div className={`vis-recommender__container__empty`}></div>
			</div>
		);
	}

	_renderSinglePreviewItem({ spec, isHighlighted = false }, index) {

		const {
			predictData,
			isPredictPreview,
			spinnerSize,
			featureSet,
			additionalProperties,

			// TODO: use from context
			dashboardApi,
			logger
		} = this.props;

		const context = {
			dashboardApi,
			spec
		};

		return(
			<VisualizationContainer
				context={context}
				predictData={predictData}
				isPredictPreview={isPredictPreview}
				onTitleChange={this.onTitleChange}
				spinnerSize={spinnerSize}
				featureSet={featureSet}
				logger={logger}
				onPromptingError={this._onPromptingError}
				additionalProperties={additionalProperties}
				isHighlighted={isHighlighted}
				key={index}
			/>
		);
	}

	renderTop() {

		if (this.props.isNewLayoutEnabled) {
			return;
		} else {
			if (this.props.titleOnBottom) {
				return;
			}

			return (
				<div className={'vis-recommender__preview__top'}>
					<Label label={this.name} ellipsis />
				</div>
			);
		}
	}

	renderBottom() {
		let title;
		if (!this.props.noBottomTitle) {
			if (!this.props.isNewLayoutEnabled && this.props.titleOnBottom ) {
				title = (
					<CSSTransition
						in={!this.state.adding}
						classNames={'vis-recommender__animation--fade'}
						timeout={200}
						appear={true}
						mountOnEnter={true}
						unmountOnExit={true}
					>
						<Label className="vis-recommender__preview__bottom__container__title" label={this.name} ellipsis />
					</CSSTransition>
				);
			}
		}

		const notification = (
			<CSSTransition
				in={this.state.adding}
				classNames={'vis-recommender__animation--fade'}
				timeout={200}
				appear={true}
				mountOnEnter={true}
				unmountOnExit={true}
			>
				<div className={`${CLASS_NAME}__bottom__container__notification`} onClick={(e) => e.preventDefault()}>
					<SVGIcon className={`${CLASS_NAME}__bottom__container__notification__icon`} intent={'positive'} iconId={confirm16.id} size="small" />
					<span className={`${CLASS_NAME}__bottom__container__notification__text`}>{StringResources.get('added_to_cards')}</span>
				</div>
			</CSSTransition>
		);

		const addButton = this.renderAddButton();

		if (title || addButton) {
			return (
				<div className="vis-recommender__preview__bottom">
					<div className={`${CLASS_NAME}__bottom__container`}>
						{title ? title : undefined}
						{notification}
					</div>
					{addButton}
				</div>
			);
		}
	}

	onTitleChange = (title) => {
		if (this.name === '' && title){
			this.name = title;
		}
	}

	renderAddButton() {
		if (this.props.add) {
			return (
				<Link
					className={`${CLASS_NAME}__bottom__add`}
					href="#"
					tabIndex="0"
					onKeyDown={this.handleKeyAdd}
					onClick={this.handleClickAdd}
					title={StringResources.get('add')}
					aria-label={StringResources.get('add')}
				>
					<Label
						className={`${CLASS_NAME}__bottom__add__text`}
						label={StringResources.get('add_label')}
					/>
					<SVGIcon
						size="small"
						iconId={addNew16.id}
					/>
				</Link>
			);
		}
	}

	handleClickAdd = e => {
		e.preventDefault();
		this.handleAdd();
		const item = cloneItem(this.props.item);
		this.props.add(item);
	}

	handleKeyAdd = e => {
		if (e.key === 'Enter') {
			e.preventDefault();
			this.handleAdd();
			const item = cloneItem(this.props.item);
			this.props.add(item);
		}
	}

	handleAdd = () => {
		this.setState({
			adding: true
		});
		if (this._pendingAddTimeoutId) {
			clearTimeout(this._pendingAddTimeoutId);
		}
		this._pendingAddTimeoutId = setTimeout(() => {
			this.setState({
				adding: false
			});
		}, 2000);
	}

	_onPromptingError = () => {
		this.props.onPromptingError(this.props.item);
	}

	_onSelect(e) {
		if (_.isFunction(this.props.onClick) && !e.isDefaultPrevented()) {
			e.preventDefault();
			const item = cloneItem(this.props.item);
			this.props.onClick(item, this.props.index);
		}
	}

	_onKeyDown(e) {
		if (e.keyCode === 13 || e.keyCode === 32) {
			this._onSelect(e);
		}
	}

}

VisualizationPreview.propTypes = {
	add: PropTypes.func,
	delete: PropTypes.func,
	isVizTypeLocked: PropTypes.bool,
	predictData: PropTypes.object,
	isPredictPreview: PropTypes.bool,
	item: PropTypes.shape({
		name: PropTypes.string,
		staticTitle: PropTypes.string,
		widgets: PropTypes.arrayOf(
			PropTypes.shape({
				spec: PropTypes.object
			})
		)
	}),
	index: PropTypes.number,
	dashboardApi: PropTypes.object.isRequired,
	onClick: PropTypes.func,
	titleOnBottom: PropTypes.bool,
	horizontal: PropTypes.bool,
	spinnerSize: PropTypes.oneOf(['small', 'normal', 'large']),
	featureSet: PropTypes.array,
	logger: PropTypes.object,
	onPromptingError: PropTypes.func,
	adding: PropTypes.bool,
	isNewLayoutEnabled: PropTypes.bool,
	additionalProperties: PropTypes.arrayOf(PropTypes.object),
	noBottomTitle: PropTypes.bool,
	size: PropTypes.oneOf(['small', 'medium', 'large'])
};

VisualizationPreview.defaultProps = {
	noBottomTitle: false,
	titleOnBottom: true,
	horizontal: false,
	spinnerSize: 'normal',
	featureSet: [],
	adding: false,
	additionalProperties: [],
	size: 'small'
};
export default VisualizationPreview;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-recommender/src/js/preview/VisualizationPreview.js